/**
 * Quality Report Tab Component
 * 
 * Displays quality issues in AI-generated summaries:
 * - Missing county data in geographic summaries
 * - Zero competitors in competitive summaries
 * - Data quality flags in combined summaries
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  LinearProgress
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  Grade as GradeIcon
} from '@mui/icons-material';
import { getQualityReport, getAcademicNaAudit } from '../../../services/aiService';

const QualityReportTab = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [severityFilter, setSeverityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Sorting
  const [orderBy, setOrderBy] = useState('school_name');
  const [order, setOrder] = useState('asc');
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Academic N/A Audit state
  const [auditExpanded, setAuditExpanded] = useState(false);
  const [auditLoaded, setAuditLoaded] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const [auditSearchTerm, setAuditSearchTerm] = useState('');
  const [auditNaFilter, setAuditNaFilter] = useState('any'); // 'any' | 'all'
  const [auditOrderBy, setAuditOrderBy] = useState('school_name');
  const [auditOrder, setAuditOrder] = useState('asc');
  const [auditPage, setAuditPage] = useState(0);
  const [auditRowsPerPage, setAuditRowsPerPage] = useState(25);

  const loadReport = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getQualityReport(severityFilter);
      setReport(data);
    } catch (err) {
      console.error('Failed to load quality report:', err);
      setError(err.message || 'Failed to load quality report');
    } finally {
      setLoading(false);
    }
  }, [severityFilter]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const loadAuditData = useCallback(async () => {
    try {
      setAuditLoading(true);
      setAuditError(null);
      const data = await getAcademicNaAudit();
      setAuditData(data);
      setAuditLoaded(true);
    } catch (err) {
      console.error('Failed to load academic N/A audit:', err);
      setAuditError(err.message || 'Failed to load audit data');
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const handleAuditAccordionChange = (_, expanded) => {
    setAuditExpanded(expanded);
    if (expanded && !auditLoaded) {
      loadAuditData();
    }
  };

  const handleAuditSort = (property) => {
    const isAsc = auditOrderBy === property && auditOrder === 'asc';
    setAuditOrder(isAsc ? 'desc' : 'asc');
    setAuditOrderBy(property);
    setAuditPage(0);
  };

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const exportToCSV = () => {
    if (!report || !report.schools) return;
    
    const rows = [];
    rows.push(['School UUID', 'School Name', 'Issue Type', 'Severity', 'Drive Time', 'Summary Type', 'Details']);
    
    report.schools.forEach(school => {
      school.issues.forEach(issue => {
        rows.push([
          school.school_uuid,
          school.school_name,
          issue.type,
          issue.severity,
          issue.drive_time || '',
          issue.summary_type || '',
          issue.details
        ]);
      });
    });
    
    const csv = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai_summary_quality_report_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const exportAuditToCSV = () => {
    if (!auditData?.affected?.length) return;
    const rows = [['School UUID', 'School Name', 'State', 'Drive Time (min)', 'N/A Count', 'Total Competitors', 'N/A %', 'Generated At']];
    auditData.affected.forEach(row => {
      rows.push([
        row.school_uuid,
        row.school_name,
        row.state || '',
        row.drive_time || '',
        row.na_count,
        row.total_competitors,
        row.na_pct,
        row.generated_at || ''
      ]);
    });
    const csv = rows.map(r => r.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `academic_na_audit_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high':
        return <ErrorIcon color="error" />;
      case 'medium':
        return <WarningIcon color="warning" />;
      case 'low':
        return <InfoIcon color="info" />;
      default:
        return <InfoIcon />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const getIssueTypeLabel = (type) => {
    if (type === 'missing_county_data') return 'Missing County Data';
    if (type === 'zero_competitors') return 'Zero Competitors';
    if (type.startsWith('text_quality_')) {
      return 'Text Quality Issue';
    }
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Filter and sort audit rows
  const filteredAuditRows = (auditData?.affected || []).filter(row => {
    if (auditNaFilter === 'all' && row.na_count !== row.total_competitors) return false;
    if (!auditSearchTerm) return true;
    const term = auditSearchTerm.toLowerCase();
    return (
      (row.school_name || '').toLowerCase().includes(term) ||
      (row.school_uuid || '').toLowerCase().includes(term) ||
      (row.state || '').toLowerCase().includes(term)
    );
  });

  const sortedAuditRows = [...filteredAuditRows].sort((a, b) => {
    let aVal, bVal;
    switch (auditOrderBy) {
      case 'school_name': aVal = a.school_name || ''; bVal = b.school_name || ''; break;
      case 'state': aVal = a.state || ''; bVal = b.state || ''; break;
      case 'drive_time': aVal = a.drive_time || 0; bVal = b.drive_time || 0; break;
      case 'na_count': aVal = a.na_count; bVal = b.na_count; break;
      case 'na_pct': aVal = a.na_pct; bVal = b.na_pct; break;
      case 'generated_at': aVal = a.generated_at || ''; bVal = b.generated_at || ''; break;
      default: return 0;
    }
    if (auditOrder === 'asc') return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
  });

  const paginatedAuditRows = sortedAuditRows.slice(
    auditPage * auditRowsPerPage,
    auditPage * auditRowsPerPage + auditRowsPerPage
  );

  // Filter and sort schools
  const filteredSchools = report?.schools?.filter(school => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return school.school_name.toLowerCase().includes(term) || 
             school.school_uuid.toLowerCase().includes(term);
    }
    return true;
  }) || [];

  const sortedSchools = filteredSchools.sort((a, b) => {
    let aValue, bValue;
    
    switch (orderBy) {
      case 'school_name':
        aValue = a.school_name;
        bValue = b.school_name;
        break;
      case 'issue_count':
        aValue = a.issues.length;
        bValue = b.issues.length;
        break;
      case 'highest_severity':
        const severityOrder = { high: 3, medium: 2, low: 1 };
        aValue = Math.max(...a.issues.map(i => severityOrder[i.severity] || 0));
        bValue = Math.max(...b.issues.map(i => severityOrder[i.severity] || 0));
        break;
      default:
        return 0;
    }
    
    if (order === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const paginatedSchools = sortedSchools.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress sx={{ mr: 2 }} />
        <Typography>Loading quality report...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={loadReport} sx={{ ml: 2 }}>Retry</Button>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Schools</Typography>
              <Typography variant="h4">{report?.summary?.total_schools || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ borderLeft: 3, borderColor: 'error.main' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color="error" />
                <Typography color="textSecondary" gutterBottom>Schools with Issues</Typography>
              </Box>
              <Typography variant="h4">{report?.summary?.schools_with_issues || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ borderLeft: 3, borderColor: 'warning.main' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">Missing County</Typography>
              <Typography variant="h5">{report?.summary?.issue_breakdown?.missing_county_data || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ borderLeft: 3, borderColor: 'warning.main' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">Zero Competitors</Typography>
              <Typography variant="h5">{report?.summary?.issue_breakdown?.zero_competitors || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <Card sx={{ borderLeft: 3, borderColor: 'info.main' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">Text Quality</Typography>
              <Typography variant="h5">{report?.summary?.issue_breakdown?.text_quality_flags || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Actions */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Severity Filter</InputLabel>
              <Select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                label="Severity Filter"
              >
                <MenuItem value="all">All Severities</MenuItem>
                <MenuItem value="high">High Only</MenuItem>
                <MenuItem value="medium">Medium Only</MenuItem>
                <MenuItem value="low">Low Only</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              label="Search Schools"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by school name or UUID..."
            />
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Tooltip title="Refresh Report">
              <IconButton onClick={loadReport} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToCSV}
              disabled={!report?.schools?.length}
            >
              Export CSV
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Results Table */}
      {report?.schools?.length === 0 ? (
        <Alert severity="success" icon={<CheckCircleIcon />}>
          <Typography variant="h6">No Quality Issues Found!</Typography>
          <Typography variant="body2">
            All AI summaries passed quality validation checks.
          </Typography>
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'school_name'}
                    direction={orderBy === 'school_name' ? order : 'asc'}
                    onClick={() => handleSort('school_name')}
                  >
                    School Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'issue_count'}
                    direction={orderBy === 'issue_count' ? order : 'asc'}
                    onClick={() => handleSort('issue_count')}
                  >
                    Issue Count
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'highest_severity'}
                    direction={orderBy === 'highest_severity' ? order : 'asc'}
                    onClick={() => handleSort('highest_severity')}
                  >
                    Highest Severity
                  </TableSortLabel>
                </TableCell>
                <TableCell>Issues</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedSchools.map((school) => {
                const highestSeverity = school.issues.reduce((max, issue) => {
                  const severityOrder = { high: 3, medium: 2, low: 1 };
                  const issueSeverity = severityOrder[issue.severity] || 0;
                  return issueSeverity > (severityOrder[max] || 0) ? issue.severity : max;
                }, 'low');

                return (
                  <TableRow key={school.school_uuid}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {school.school_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {school.school_uuid}
                      </Typography>
                    </TableCell>
                    <TableCell>{school.issues.length}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getSeverityIcon(highestSeverity)}
                        label={highestSeverity.toUpperCase()}
                        color={getSeverityColor(highestSeverity)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {school.issues.map((issue, idx) => (
                          <Box key={idx} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip
                              label={getIssueTypeLabel(issue.type)}
                              size="small"
                              variant="outlined"
                            />
                            <Chip
                              icon={getSeverityIcon(issue.severity)}
                              label={issue.severity}
                              color={getSeverityColor(issue.severity)}
                              size="small"
                            />
                            <Typography variant="caption" color="textSecondary">
                              {issue.drive_time}min - {issue.summary_type}
                            </Typography>
                            <Tooltip title={issue.details}>
                              <Typography
                                variant="caption"
                                sx={{
                                  maxWidth: 300,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {issue.details}
                              </Typography>
                            </Tooltip>
                          </Box>
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={sortedSchools.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Academic N/A Audit Section */}
      <Divider sx={{ my: 4 }} />
      <Accordion expanded={auditExpanded} onChange={handleAuditAccordionChange}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
            <GradeIcon color="warning" />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Academic Ratings N/A Audit
            </Typography>
            {auditData && (
              <Chip
                label={`${auditData.summary.affected_summaries} affected`}
                color={auditData.summary.affected_summaries > 0 ? 'warning' : 'success'}
                size="small"
                sx={{ mr: 1 }}
              />
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Competitive summaries where <strong>academic_ratings</strong> is N/A for one or more
            competitors, even though <code>school_academics</code> data exists for that state.
            These summaries need to be regenerated now that the underlying code bug is fixed.
          </Typography>

          {auditLoading && (
            <Box sx={{ py: 4 }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="textSecondary" align="center">
                Running audit across all stored competitive summaries…
              </Typography>
            </Box>
          )}

          {auditError && (
            <Alert severity="error" sx={{ mb: 2 }}
              action={<Button size="small" onClick={loadAuditData}>Retry</Button>}>
              {auditError}
            </Alert>
          )}

          {auditData && !auditLoading && (
            <>
              {/* Summary cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Card sx={{ borderLeft: 3, borderColor: auditData.summary.affected_summaries > 0 ? 'warning.main' : 'success.main' }}>
                    <CardContent>
                      <Typography color="textSecondary" variant="body2" gutterBottom>
                        Affected Summaries
                      </Typography>
                      <Typography variant="h4">
                        {auditData.summary.affected_summaries}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        of {auditData.summary.total_competitive_summaries} total
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" variant="body2" gutterBottom>
                        States with Academic Data
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {auditData.summary.states_with_academic_data.map(st => (
                          <Chip key={st} label={st} size="small" variant="outlined" />
                        ))}
                        {auditData.summary.states_with_academic_data.length === 0 && (
                          <Typography variant="body2" color="textSecondary">None</Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" variant="body2" gutterBottom>
                        Affected by State
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {Object.entries(auditData.summary.affected_by_state).map(([st, count]) => (
                          <Chip key={st} label={`${st}: ${count}`} size="small" color="warning" />
                        ))}
                        {Object.keys(auditData.summary.affected_by_state).length === 0 && (
                          <Typography variant="body2" color="textSecondary">None</Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {auditData.summary.affected_summaries === 0 ? (
                <Alert severity="success" icon={<CheckCircleIcon />}>
                  <Typography variant="body1" fontWeight="medium">All clear!</Typography>
                  <Typography variant="body2">
                    No stored competitive summaries have N/A academic ratings for states where data exists.
                  </Typography>
                </Alert>
              ) : (
                <>
                  {/* Filters + export */}
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>N/A Filter</InputLabel>
                          <Select
                            value={auditNaFilter}
                            onChange={(e) => { setAuditNaFilter(e.target.value); setAuditPage(0); }}
                            label="N/A Filter"
                          >
                            <MenuItem value="any">Any competitor N/A</MenuItem>
                            <MenuItem value="all">All competitors N/A (100%)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Search Schools"
                          value={auditSearchTerm}
                          onChange={(e) => { setAuditSearchTerm(e.target.value); setAuditPage(0); }}
                          placeholder="Search by name, UUID, or state…"
                        />
                      </Grid>
                      <Grid item xs={12} md={4} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Tooltip title="Refresh audit">
                          <IconButton onClick={loadAuditData} color="primary">
                            <RefreshIcon />
                          </IconButton>
                        </Tooltip>
                        <Button
                          variant="outlined"
                          startIcon={<DownloadIcon />}
                          onClick={exportAuditToCSV}
                          disabled={!auditData?.affected?.length}
                        >
                          Export CSV
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Audit table */}
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel
                              active={auditOrderBy === 'school_name'}
                              direction={auditOrderBy === 'school_name' ? auditOrder : 'asc'}
                              onClick={() => handleAuditSort('school_name')}
                            >
                              School Name
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={auditOrderBy === 'state'}
                              direction={auditOrderBy === 'state' ? auditOrder : 'asc'}
                              onClick={() => handleAuditSort('state')}
                            >
                              State
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={auditOrderBy === 'drive_time'}
                              direction={auditOrderBy === 'drive_time' ? auditOrder : 'asc'}
                              onClick={() => handleAuditSort('drive_time')}
                            >
                              Drive Time
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={auditOrderBy === 'na_count'}
                              direction={auditOrderBy === 'na_count' ? auditOrder : 'asc'}
                              onClick={() => handleAuditSort('na_count')}
                            >
                              N/A Competitors
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={auditOrderBy === 'na_pct'}
                              direction={auditOrderBy === 'na_pct' ? auditOrder : 'asc'}
                              onClick={() => handleAuditSort('na_pct')}
                            >
                              N/A %
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={auditOrderBy === 'generated_at'}
                              direction={auditOrderBy === 'generated_at' ? auditOrder : 'asc'}
                              onClick={() => handleAuditSort('generated_at')}
                            >
                              Generated At
                            </TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedAuditRows.map((row) => (
                          <TableRow key={`${row.school_uuid}-${row.drive_time}`} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {row.school_name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {row.school_uuid}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={row.state || '—'} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>{row.drive_time ? `${row.drive_time} min` : '—'}</TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {row.na_count} / {row.total_competitors}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 80 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={row.na_pct}
                                  color={row.na_pct === 100 ? 'error' : 'warning'}
                                  sx={{ flexGrow: 1, height: 6, borderRadius: 3 }}
                                />
                                <Typography variant="caption">{row.na_pct}%</Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="textSecondary">
                                {row.generated_at
                                  ? new Date(row.generated_at).toLocaleDateString('en-US', {
                                      year: 'numeric', month: 'short', day: 'numeric'
                                    })
                                  : '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      component="div"
                      count={sortedAuditRows.length}
                      rowsPerPage={auditRowsPerPage}
                      page={auditPage}
                      onPageChange={(_, newPage) => setAuditPage(newPage)}
                      onRowsPerPageChange={(e) => { setAuditRowsPerPage(parseInt(e.target.value, 10)); setAuditPage(0); }}
                    />
                  </TableContainer>
                </>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default QualityReportTab;
